import React from "react";

type IdsContextType = {
  showCodes: boolean;
  toggleShowCodes: () => void;
  getCodeForId: (rawId: string | number | undefined) => string;
  getCodeForDriverAndId: (
    driverCarNum: string | number | undefined,
    rawId: string | number | undefined,
  ) => string;
};

type DriverCodeSource = {
  carNum: string;
  stints: Array<{
    tpmsAtStart?: {
      frontLeft?: number | null;
      frontRight?: number | null;
      rearLeft?: number | null;
      rearRight?: number | null;
    } | null;
  }>;
};

export const IdsContext = React.createContext<IdsContextType | undefined>(
  undefined,
);

function buildDriverCodeMap(drivers: DriverCodeSource[]): Map<string, string> {
  const codeMap = new Map<string, string>();
  const seenScopedIds = new Set<string>();
  const allTyreIds: {
    id: string;
    position: "front" | "rear";
    driverCarNum: string;
  }[] = [];

  for (const driver of drivers) {
    for (const stint of driver.stints) {
      const tpms = stint.tpmsAtStart;
      if (tpms?.frontLeft) {
        const id = String(tpms.frontLeft);
        const scopedId = `${driver.carNum}:${id}`;
        if (!seenScopedIds.has(scopedId)) {
          allTyreIds.push({
            id,
            position: "front",
            driverCarNum: driver.carNum,
          });
          seenScopedIds.add(scopedId);
        }
      }
      if (tpms?.frontRight) {
        const id = String(tpms.frontRight);
        const scopedId = `${driver.carNum}:${id}`;
        if (!seenScopedIds.has(scopedId)) {
          allTyreIds.push({
            id,
            position: "front",
            driverCarNum: driver.carNum,
          });
          seenScopedIds.add(scopedId);
        }
      }
      if (tpms?.rearLeft) {
        const id = String(tpms.rearLeft);
        const scopedId = `${driver.carNum}:${id}`;
        if (!seenScopedIds.has(scopedId)) {
          allTyreIds.push({
            id,
            position: "rear",
            driverCarNum: driver.carNum,
          });
          seenScopedIds.add(scopedId);
        }
      }
      if (tpms?.rearRight) {
        const id = String(tpms.rearRight);
        const scopedId = `${driver.carNum}:${id}`;
        if (!seenScopedIds.has(scopedId)) {
          allTyreIds.push({
            id,
            position: "rear",
            driverCarNum: driver.carNum,
          });
          seenScopedIds.add(scopedId);
        }
      }
    }
  }

  const driverCounters = new Map<
    string,
    { frontCount: number; rearCount: number }
  >();

  for (const { id, position, driverCarNum } of allTyreIds) {
    if (!driverCounters.has(driverCarNum)) {
      driverCounters.set(driverCarNum, { frontCount: 1, rearCount: 1 });
    }

    const counters = driverCounters.get(driverCarNum);
    if (!counters) continue;

    const code =
      position === "front"
        ? `F${String(counters.frontCount++).padStart(2, "0")}`
        : `R${String(counters.rearCount++).padStart(2, "0")}`;

    codeMap.set(`${driverCarNum}:${id}`, code);
  }

  return codeMap;
}

export const IdsProvider: React.FC<{
  children: React.ReactNode;
  drivers?: DriverCodeSource[];
}> = ({ children, drivers = [] }) => {
  const [showCodes, setShowCodes] = React.useState(true);
  const idMap = React.useMemo(() => buildDriverCodeMap(drivers), [drivers]);

  const toggleShowCodes = React.useCallback(() => {
    setShowCodes((prev) => {
      console.log("Toggling showCodes from", prev, "to", !prev);
      return !prev;
    });
  }, []);

  const getCodeForId = React.useCallback(
    (rawId: string | number | undefined) => {
      if (!rawId) return "-";
      const rawIdKey = String(rawId);
      return idMap.get(rawIdKey) || rawIdKey;
    },
    [idMap],
  );

  const getCodeForDriverAndId = React.useCallback(
    (
      driverCarNum: string | number | undefined,
      rawId: string | number | undefined,
    ) => {
      if (!rawId) return "-";
      const rawIdKey = String(rawId);
      const driverKey = driverCarNum ? String(driverCarNum) : "";
      const scopedKey = driverKey ? `${driverKey}:${rawIdKey}` : rawIdKey;
      return idMap.get(scopedKey) || idMap.get(rawIdKey) || rawIdKey;
    },
    [idMap],
  );

  const contextValue = React.useMemo(
    () => ({
      showCodes,
      toggleShowCodes,
      getCodeForId,
      getCodeForDriverAndId,
    }),
    [showCodes, toggleShowCodes, getCodeForId, getCodeForDriverAndId],
  );

  return (
    <IdsContext.Provider value={contextValue}>{children}</IdsContext.Provider>
  );
};
